// exercise_catalog.js — the 870+ exercise catalog (public-domain free-exercise-db,
// see tools/build-exercise-db.mjs). Loaded lazily as its own chunk so first paint
// stays fast; the service worker precaches it for offline use.
//
// The catalog is static reference data and is NOT stored in app state. When the
// user adds a catalog exercise to a program (or logs it in a freestyle workout),
// `catalogToExercise` copies a compact entry into state.exercises so history and
// name lookups never depend on the catalog having loaded.

import React from 'react';

let _cache = null;
let _promise = null;

export function loadCatalog() {
  if (_cache) return Promise.resolve(_cache);
  if (!_promise) {
    _promise = import('./exercise_db.json').then(m => {
      _cache = m.default || m;
      return _cache;
    }).catch(err => {
      _promise = null; // allow a retry (e.g. first load while offline before SW cached it)
      throw err;
    });
  }
  return _promise;
}

export function getCatalogSync() { return _cache; }

// React hook: returns the catalog array, or null while it loads.
export function useCatalog() {
  const [cat, setCat] = React.useState(_cache);
  React.useEffect(() => {
    if (cat) return;
    let alive = true;
    loadCatalog().then(c => { if (alive) setCat(c); }).catch(() => {});
    return () => { alive = false; };
  }, [cat]);
  return cat;
}

const IMG_BASE = 'https://cdn.jsdelivr.net/gh/yuhonas/free-exercise-db@main/exercises/';
export function exerciseImageUrl(path) {
  return path ? IMG_BASE + path : '';
}

// Built-in (legacy) exercise ids → catalog ids, so the original 35 exercises
// get photos, instructions and secondary muscles without changing their ids
// (existing history references them).
export const BUILTIN_MEDIA = {
  x_incsmith: 'fx_Smith_Machine_Incline_Bench_Press',
  x_flatdb: 'fx_Dumbbell_Bench_Press',
  x_pecdeck: 'fx_Butterfly',
  x_bench: 'fx_Barbell_Bench_Press_-_Medium_Grip',
  x_cflies: 'fx_Cable_Crossover',
  x_pullup: 'fx_Pullups',
  x_csrow: 'fx_Dumbbell_Incline_Row',
  x_lat: 'fx_Wide-Grip_Lat_Pulldown',
  x_cablerow: 'fx_Seated_Cable_Rows',
  x_dead: 'fx_Barbell_Deadlift',
  x_ohp: 'fx_Standing_Military_Press',
  x_dbohp: 'fx_Dumbbell_Shoulder_Press',
  x_mohp: 'fx_Machine_Shoulder_Military_Press',
  x_lat_r: 'fx_Cable_Seated_Lateral_Raise',
  x_facepull: 'fx_Face_Pull',
  x_squat: 'fx_Barbell_Squat',
  x_fsquat: 'fx_Front_Barbell_Squat',
  x_hacksq: 'fx_Hack_Squat',
  x_legpress: 'fx_Leg_Press',
  x_bss: 'fx_Split_Squat_with_Dumbbells',
  x_rdl: 'fx_Romanian_Deadlift',
  x_slc: 'fx_Seated_Leg_Curl',
  x_llc: 'fx_Lying_Leg_Curls',
  x_hipthr: 'fx_Barbell_Hip_Thrust',
  x_scalf: 'fx_Standing_Calf_Raises',
  x_sitcalf: 'fx_Seated_Calf_Raise',
  x_ezcurl: 'fx_EZ-Bar_Curl',
  x_preach: 'fx_Preacher_Curl',
  x_hammer: 'fx_Hammer_Curls',
  x_push: 'fx_Triceps_Pushdown',
  x_ohext: 'fx_Cable_Rope_Overhead_Triceps_Extension',
  x_skull: 'fx_EZ-Bar_Skullcrusher',
  x_plank: 'fx_Plank',
  x_abwheel: 'fx_Ab_Roller',
  x_hlr: 'fx_Hanging_Leg_Raise',
};

// Catalog entry for any exercise id (catalog id directly, or a built-in via BUILTIN_MEDIA).
export function catalogEntryFor(id, catalog = _cache) {
  if (!id || !catalog) return null;
  const cid = id.startsWith('fx_') ? id : BUILTIN_MEDIA[id];
  if (!cid) return null;
  return catalog.find(e => e.id === cid) || null;
}

// Compact copy suitable for state.exercises (drops the long instruction text).
export function catalogToExercise(e) {
  const x = {
    id: e.id, name: e.name, group: e.group, equipment: e.equipment,
    mechanic: e.mechanic, primary: e.primary, secondary: e.secondary,
    img: e.img, yt: '', catalog: true,
  };
  if (e.unit) x.unit = e.unit;
  if (e.category) x.category = e.category;
  return x;
}
